/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数。 親コンポーネント再 render 時に再生成されるが、
 * MasterDataTable は cell の戻り値を <TableCell> の children として表示するだけで
 * 独立した component subtree を持たないため、 lint 警告の前提 (= subtree 破棄)
 * に該当しない。
 */
import { useTranslation } from 'react-i18next'

import {
  AuditCell,
  MasterDataTable,
  type MasterDataTableColumn,
  standardRowActions,
} from '~/components/common/MasterDataTable'
import { convertAreaLabel } from '~/models/area'
import type { CivilDate } from '~/models/civilDate'
import { licenseTypeLabel } from '~/models/licenseType'
import { CellStack } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/components/CellStack'
import type { SubmitterMasterRow } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useListSubmitterMasters'
import { ExpiredBadge } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredBadge'
import { formatCivilDate } from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type Props = {
  rows: SubmitterMasterRow[]
  isLoading: boolean
  onEdit: (row: SubmitterMasterRow) => void
  onDelete: (row: SubmitterMasterRow) => void
  today: CivilDate
  /** 空 state のメッセージ上書き (AC-09)。 */
  emptyLabel?: string
  /** 一覧取得エラー時のメッセージ。 */
  errorLabel?: string
}

export const SubmitterMasterTable = ({
  rows,
  isLoading,
  onEdit,
  onDelete,
  today,
  emptyLabel,
  errorLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const columns: Array<MasterDataTableColumn<SubmitterMasterRow>> = [
    // [design 差異] ステータスは全画面共通で列の先頭に置く (2026-08-27 デザイナー判断)。
    // design (`design/admin/occto.html` 計画提出者タブ) は 7 列目 (事業者コード・名称 と
    // 適用開始日 の間。 静的 thead には無く _occtoSyncStatusColumn が挿入する) だが、
    // 行の状態は他の属性を読む前に分かる必要があるため一覧の左端へ統一した。
    {
      key: 'status',
      header: t('page:adminOcctoMaster.common.column.status'),
      cell: (row) => (
        <ExpiredBadge
          today={today}
          validFrom={row.validFrom}
          validTo={row.validTo}
        />
      ),
    },
    {
      key: 'submitterCode',
      header: t('page:adminOcctoMaster.submitterMaster.column.submitterCode'),
      cell: (row) => row.submitterCode,
    },
    {
      key: 'submitterName',
      header: t('page:adminOcctoMaster.submitterMaster.column.submitterName'),
      cell: (row) => row.submitterName,
    },
    {
      key: 'areaCode',
      header: t('page:adminOcctoMaster.submitterMaster.column.areaCode'),
      cell: (row) => convertAreaLabel(row.areaCode),
    },
    {
      key: 'licenseType',
      header: t('page:adminOcctoMaster.submitterMaster.column.licenseType'),
      cell: (row) => licenseTypeLabel(row.licenseType),
    },
    {
      key: 'memberCount',
      header: t('page:adminOcctoMaster.submitterMaster.column.memberCount'),
      format: 'number',
      cell: (row) => row.memberCount.toLocaleString(),
    },
    {
      // AC-10 (occto-master-audit-fix-bundle.md): 「最終更新」の直前に配置
      key: 'businessCode',
      header: t('page:adminOcctoMaster.submitterMaster.column.businessCode'),
      cell: (row) => (
        <CellStack main={row.businessCode} sub={row.businessName} />
      ),
    },
    {
      key: 'validFrom',
      header: t('page:adminOcctoMaster.common.column.validFrom'),
      cell: (row) => formatCivilDate(row.validFrom),
    },
    {
      key: 'validTo',
      header: t('page:adminOcctoMaster.common.column.validTo'),
      cell: (row) =>
        row.validTo !== undefined ? formatCivilDate(row.validTo) : '—',
    },
    {
      key: 'updatedAt',
      header: t('page:adminOcctoMaster.submitterMaster.column.updatedAt'),
      nowrap: true,
      cell: (row) => (
        <AuditCell updatedAt={row.updatedAt} updatedBy={row.updatedBy} />
      ),
    },
  ]

  return (
    <MasterDataTable
      columns={columns}
      emptyLabel={emptyLabel}
      errorLabel={errorLabel}
      getRowKey={(row) => row.id}
      isLoading={isLoading}
      onRowClick={onEdit}
      renderRowActions={standardRowActions(onEdit, onDelete)}
      rows={rows}
    />
  )
}
